import React, { useState } from 'react';
import { Button } from '../Button';
import { UploadIcon, SparklesIcon, PlusIcon, XIcon } from 'lucide-react';
interface PayloadEditorProps {
  value: string;
  onChange: (value: string) => void;
  isNewEndpoint?: boolean;
  transactionType?: 'transfers' | 'payments';
  onTransactionTypeChange?: (type: 'transfers' | 'payments') => void;
}
interface InferredField {
  path: string;
  type: 'String' | 'Number' | 'Boolean' | 'Object' | 'Array';
  parent?: string;
  level: number;
}
interface ConstantField {
  name: string;
  type: 'String' | 'Number' | 'Boolean';
  value: string;
}
interface FormulaField {
  name: string;
  type: 'String' | 'Number' | 'Boolean';
  formula: string;
}
interface AddConstantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (field: ConstantField) => void;
}
interface AddFormulaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (field: FormulaField) => void;
}
const AddConstantModal: React.FC<AddConstantModalProps> = ({
  isOpen,
  onClose,
  onAdd
}) => {
  const [field, setField] = useState<ConstantField>({
    name: '',
    type: 'String',
    value: ''
  });
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(field);
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-412">
      <div className="bg-white rounded-lg w-full max-w-md p-6" data-id="element-413">
        <div className="flex justify-between items-center mb-4" data-id="element-414">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-415">
            Add Constant Field
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-416">
            <XIcon size={20} data-id="element-417" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-418">
          <div className="grid grid-cols-2 gap-4" data-id="element-419">
            <div data-id="element-420">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-421">
                Field Name
              </label>
              <input type="text" value={field.name} onChange={e => setField({
              ...field,
              name: e.target.value
            })} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="constant" required data-id="element-422" />
            </div>
            <div data-id="element-423">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-424">
                Type
              </label>
              <select value={field.type} onChange={e => setField({
              ...field,
              type: e.target.value as any
            })} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-425">
                <option value="String" data-id="element-426">String</option>
                <option value="Number" data-id="element-427">Number</option>
                <option value="Boolean" data-id="element-428">Boolean</option>
              </select>
            </div>
          </div>
          <div data-id="element-429">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-430">
              Value
            </label>
            <input type="text" value={field.value} onChange={e => setField({
            ...field,
            value: e.target.value
          })} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Constant value" required data-id="element-431" />
          </div>
          <div className="flex justify-end space-x-3 pt-4" data-id="element-432">
            <Button variant="secondary" onClick={onClose} data-id="element-433">
              Cancel
            </Button>
            <Button variant="primary" type="submit" data-id="element-434">
              Add Constant Field
            </Button>
          </div>
        </form>
      </div>
    </div>;
};
const AddFormulaModal: React.FC<AddFormulaModalProps> = ({
  isOpen,
  onClose,
  onAdd
}) => {
  const [field, setField] = useState<FormulaField>({
    name: '',
    type: 'Number',
    formula: ''
  });
  const [selectedField, setSelectedField] = useState<string>('');
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(field);
    onClose();
  };
  const handleInsertField = () => {
    if (selectedField) {
      setField({
        ...field,
        formula: field.formula + `{${selectedField}}`
      });
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-435">
      <div className="bg-white rounded-lg w-full max-w-md p-6" data-id="element-436">
        <div className="flex justify-between items-center mb-6" data-id="element-437">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-438">
            Add Formula Field
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-439">
            <XIcon size={20} data-id="element-440" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-441">
          <div className="space-y-1" data-id="element-442">
            <label className="block text-sm font-medium text-gray-700" data-id="element-443">
              Field Name
            </label>
            <input type="text" value={field.name} onChange={e => setField({
            ...field,
            name: e.target.value
          })} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="calculated" required data-id="element-444" />
          </div>
          <div className="space-y-1" data-id="element-445">
            <label className="block text-sm font-medium text-gray-700" data-id="element-446">
              Output Type
            </label>
            <select value={field.type} onChange={e => setField({
            ...field,
            type: e.target.value as any
          })} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-447">
              <option value="Number" data-id="element-448">Number</option>
              <option value="String" data-id="element-449">String</option>
              <option value="Boolean" data-id="element-450">Boolean</option>
            </select>
          </div>
          <div className="space-y-1" data-id="element-451">
            <label className="block text-sm font-medium text-gray-700" data-id="element-452">
              Available Fields
            </label>
            <div className="flex space-x-2" data-id="element-453">
              <select value={selectedField} onChange={e => setSelectedField(e.target.value)} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-454">
                <option value="" data-id="element-455">-- Select a field to insert --</option>
                <option value="transaction.id" data-id="element-456">transaction.id</option>
                <option value="transaction.amount" data-id="element-457">transaction.amount</option>
                <option value="transaction.currency" data-id="element-458">
                  transaction.currency
                </option>
                <option value="transaction.status" data-id="element-459">transaction.status</option>
                <option value="customer.id" data-id="element-460">customer.id</option>
                <option value="customer.name" data-id="element-461">customer.name</option>
                <option value="customer.email" data-id="element-462">customer.email</option>
              </select>
              <button type="button" onClick={handleInsertField} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-463">
                Insert
              </button>
            </div>
          </div>
          <div className="space-y-1" data-id="element-464">
            <label className="block text-sm font-medium text-gray-700" data-id="element-465">
              Formula Expression
            </label>
            <input type="text" value={field.formula} onChange={e => setField({
            ...field,
            formula: e.target.value
          })} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., {items[0].price} * {quantity}" required data-id="element-466" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-467">
              Use {'{fieldPath}'} to reference other fields
            </p>
          </div>
          <div className="flex justify-end space-x-3 pt-4" data-id="element-468">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-469">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-green-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-470">
              Add Formula Field
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export const PayloadEditor: React.FC<PayloadEditorProps> = ({
  value,
  onChange,
  isNewEndpoint = false,
  transactionType = 'transfers',
  onTransactionTypeChange
}) => {
  const [format, setFormat] = useState<'json' | 'xml'>('json');
  const [inferredFields, setInferredFields] = useState<InferredField[]>([]);
  const [showInferredFields, setShowInferredFields] = useState(false);
  const [constantFields, setConstantFields] = useState<ConstantField[]>([]);
  const [formulaFields, setFormulaFields] = useState<FormulaField[]>([]);
  const [showAddConstantModal, setShowAddConstantModal] = useState(false);
  const [showAddFormulaModal, setShowAddFormulaModal] = useState(false);
  const handleTransactionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'transfers' | 'payments';
    if (onTransactionTypeChange) {
      onTransactionTypeChange(newType);
    }
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target?.result as string;
        onChange(content);
      };
      reader.readAsText(file);
    }
  };
  const inferFieldsFromJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      const fields: InferredField[] = [];
      const processObject = (obj: any, path: string = '', level: number = 0, parent: string = '') => {
        // Add the object itself if it's a root level object
        if (level === 0) {
          fields.push({
            path,
            type: 'Object',
            level
          });
        }
        Object.entries(obj).forEach(([key, value]) => {
          const currentPath = path ? `${path}.${key}` : key;
          const valueType = typeof value;
          if (valueType === 'object') {
            if (value === null) {
              fields.push({
                path: currentPath,
                type: 'String',
                parent: path,
                level: level + 1
              });
            } else if (Array.isArray(value)) {
              fields.push({
                path: currentPath,
                type: 'Array',
                parent: path,
                level: level + 1
              });
              // Process the first item in the array if available
              if (value.length > 0 && typeof value[0] === 'object') {
                processObject(value[0], currentPath, level + 1, path);
              }
            } else {
              fields.push({
                path: currentPath,
                type: 'Object',
                parent: path,
                level: level + 1
              });
              processObject(value, currentPath, level + 1, path);
            }
          } else {
            fields.push({
              path: currentPath,
              type: valueType === 'string' ? 'String' : valueType === 'number' ? 'Number' : valueType === 'boolean' ? 'Boolean' : 'String',
              parent: path,
              level: level + 1
            });
          }
        });
      };
      processObject(parsed);
      return fields;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return [];
    }
  };
  const inferFieldsFromXml = (xml: string) => {
    // A very basic XML parser - for production use, consider using a proper XML parser library
    try {
      // For demo purposes, we'll create some sample fields based on the XML structure
      const fields: InferredField[] = [];
      // Extract tag names from XML
      const tagRegex = /<(\w+)[^>]*>(?:[^<]*)<\/\1>|<(\w+)[^>]*\/>/g;
      const matches = [...xml.matchAll(tagRegex)];
      // Create a simple hierarchy based on XML structure
      const rootTag = matches[0]?.[1] || matches[0]?.[2];
      if (rootTag) {
        fields.push({
          path: rootTag,
          type: 'Object',
          level: 0
        });
        // Extract child tags
        const childTagRegex = new RegExp(`<${rootTag}[^>]*>(.*)<\\/${rootTag}>`, 's');
        const childContent = xml.match(childTagRegex)?.[1] || '';
        const childMatches = [...childContent.matchAll(tagRegex)];
        const processedTags = new Set<string>();
        childMatches.forEach(match => {
          const tagName = match[1] || match[2];
          if (tagName && !processedTags.has(tagName)) {
            processedTags.add(tagName);
            fields.push({
              path: `${rootTag}.${tagName}`,
              type: 'String',
              parent: rootTag,
              level: 1
            });
          }
        });
      }
      return fields;
    } catch (error) {
      console.error('Error parsing XML:', error);
      return [];
    }
  };
  const handleGenerateFields = () => {
    // Create sample fields to match the image
    const sampleFields: InferredField[] = [{
      path: 'transaction',
      type: 'Object',
      level: 0
    }, {
      path: 'transaction.id',
      type: 'String',
      parent: 'transaction',
      level: 1
    }, {
      path: 'transaction.amount',
      type: 'Number',
      parent: 'transaction',
      level: 1
    }, {
      path: 'transaction.currency',
      type: 'String',
      parent: 'transaction',
      level: 1
    }, {
      path: 'transaction.status',
      type: 'String',
      parent: 'transaction',
      level: 1
    }, {
      path: 'customer',
      type: 'Object',
      level: 0
    }, {
      path: 'customer.id',
      type: 'String',
      parent: 'customer',
      level: 1
    }, {
      path: 'customer.name',
      type: 'String',
      parent: 'customer',
      level: 1
    }, {
      path: 'customer.email',
      type: 'String',
      parent: 'customer',
      level: 1
    }];
    setInferredFields(sampleFields);
    setShowInferredFields(true);
  };
  const handleAddConstantField = (field: ConstantField) => {
    setConstantFields([...constantFields, field]);
  };
  const handleAddFormulaField = (field: FormulaField) => {
    setFormulaFields([...formulaFields, field]);
  };
  const sampleJsonPayload = `{
  "transaction": {
    "id": "TX12345",
    "amount": 100.5,
    "currency": "USD",
    "status": "pending"
  },
  "customer": {
    "id": "C789",
    "name": "John Doe",
    "email": "john@example.com"
  },
  "items": [
    {
      "id": "ITEM-1",
      "name": "Product A",
      "quantity": 2,
      "price": 45.25
    }
  ]
}`;
  const sampleXmlPayload = `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:acmt.023.001.02">
  <AcctId>
    <Id>12345</Id>
    <Ccy>USD</Ccy>
    <Nm>John Doe</Nm>
  </AcctId>
</Document>`;
  return <div className="space-y-4" data-id="element-471">
      {/* Transaction Type Dropdown */}
      <div className="mb-4" data-id="element-472">
        <label htmlFor="transaction-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-473">
          Transaction Type
        </label>
        <select id="transaction-type" value={transactionType} onChange={handleTransactionTypeChange} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-474">
          <option value="transfers" data-id="element-475">Transfers</option>
          <option value="payments" data-id="element-476">Payments</option>
        </select>
      </div>
      <div className="flex justify-between items-center" data-id="element-477">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-478">Sample Payload</h3>
        <div className="flex items-center space-x-4" data-id="element-479">
          <div className="flex rounded-md shadow-sm" data-id="element-480">
            <button type="button" onClick={() => setFormat('xml')} className={`px-4 py-2 text-sm font-medium rounded-l-md ${format === 'xml' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:text-gray-500 border border-gray-300'}`} data-id="element-481">
              XML
            </button>
            <button type="button" onClick={() => setFormat('json')} className={`px-4 py-2 text-sm font-medium rounded-r-md ${format === 'json' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:text-gray-500 border border-gray-300'}`} data-id="element-482">
              JSON
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-483">
            <input type="file" id="file-upload" className="hidden" accept=".xml,.json" onChange={handleFileUpload} data-id="element-484" />
            <Button variant="secondary" size="sm" icon={<UploadIcon size={16} data-id="element-486" />} onClick={() => document.getElementById('file-upload')?.click()} data-id="element-485">
              Import File
            </Button>
          </div>
        </div>
      </div>
      {/* Code Editor */}
      <div className="border rounded-md" data-id="element-487">
        <textarea value={value || (format === 'json' ? sampleJsonPayload : sampleXmlPayload)} onChange={e => onChange(e.target.value)} className="w-full h-96 p-4 font-mono text-sm bg-gray-50" spellCheck="false" placeholder={`Enter your ${format.toUpperCase()} payload here...`} data-id="element-488" />
      </div>
      {/* Format Validation Status */}
      {value && <div className="p-3 bg-green-50 border border-green-200 rounded-md" data-id="element-489">
          <p className="text-sm text-green-700" data-id="element-490">
            Valid {format.toUpperCase()} format detected
          </p>
        </div>}
      {/* Generate Schema Button */}
      <div className="flex justify-center my-6" data-id="element-491">
        <Button variant="primary" icon={<SparklesIcon size={18} data-id="element-493" />} onClick={handleGenerateFields} data-id="element-492">
          Generate Fields
        </Button>
      </div>
      {/* Inferred Fields Section */}
      {showInferredFields && inferredFields.length > 0 && <div className="mt-8 space-y-4" data-id="element-494">
          <div data-id="element-495">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-496">
              Generated Fields
            </h3>
            <p className="text-sm text-gray-600 mt-1" data-id="element-497">
              Define the structure of your {format.toUpperCase()} schema based
              on the input data. For each field, specify its data type.
            </p>
          </div>
          {/* Transaction parent field */}
          <div data-id="element-498">
            <div className="mb-2" data-id="element-499">
              <div className="grid grid-cols-2 gap-4" data-id="element-500">
                <div data-id="element-501">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-502">
                    Field Path
                  </label>
                  <input type="text" value="transaction" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-503" />
                </div>
                <div data-id="element-504">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-505">
                    Type
                  </label>
                  <select value="Object" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-506">
                    <option value="String" data-id="element-507">String</option>
                    <option value="Number" data-id="element-508">Number</option>
                    <option value="Boolean" data-id="element-509">Boolean</option>
                    <option value="Object" data-id="element-510">Object</option>
                    <option value="Array" data-id="element-511">Array</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Transaction child fields */}
            <div className="border border-gray-200 rounded-md p-4 mb-6" data-id="element-512">
              {/* transaction.id field */}
              <div className="mb-4" data-id="element-513">
                <div className="grid grid-cols-2 gap-4" data-id="element-514">
                  <div data-id="element-515">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-516">
                      Field Path
                    </label>
                    <input type="text" value="transaction.id" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-517" />
                  </div>
                  <div data-id="element-518">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-519">
                      Type
                    </label>
                    <select value="String" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-520">
                      <option value="String" data-id="element-521">String</option>
                      <option value="Number" data-id="element-522">Number</option>
                      <option value="Boolean" data-id="element-523">Boolean</option>
                      <option value="Object" data-id="element-524">Object</option>
                      <option value="Array" data-id="element-525">Array</option>
                    </select>
                  </div>
                </div>
              </div>
              {/* transaction.amount field */}
              <div className="mb-4" data-id="element-526">
                <div className="grid grid-cols-2 gap-4" data-id="element-527">
                  <div data-id="element-528">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-529">
                      Field Path
                    </label>
                    <input type="text" value="transaction.amount" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-530" />
                  </div>
                  <div data-id="element-531">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-532">
                      Type
                    </label>
                    <select value="Number" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-533">
                      <option value="String" data-id="element-534">String</option>
                      <option value="Number" data-id="element-535">Number</option>
                      <option value="Boolean" data-id="element-536">Boolean</option>
                      <option value="Object" data-id="element-537">Object</option>
                      <option value="Array" data-id="element-538">Array</option>
                    </select>
                  </div>
                </div>
              </div>
              {/* transaction.currency field */}
              <div className="mb-4" data-id="element-539">
                <div className="grid grid-cols-2 gap-4" data-id="element-540">
                  <div data-id="element-541">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-542">
                      Field Path
                    </label>
                    <input type="text" value="transaction.currency" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-543" />
                  </div>
                  <div data-id="element-544">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-545">
                      Type
                    </label>
                    <select value="String" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-546">
                      <option value="String" data-id="element-547">String</option>
                      <option value="Number" data-id="element-548">Number</option>
                      <option value="Boolean" data-id="element-549">Boolean</option>
                      <option value="Object" data-id="element-550">Object</option>
                      <option value="Array" data-id="element-551">Array</option>
                    </select>
                  </div>
                </div>
              </div>
              {/* transaction.status field */}
              <div data-id="element-552">
                <div className="grid grid-cols-2 gap-4" data-id="element-553">
                  <div data-id="element-554">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-555">
                      Field Path
                    </label>
                    <input type="text" value="transaction.status" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-556" />
                  </div>
                  <div data-id="element-557">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-558">
                      Type
                    </label>
                    <select value="String" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-559">
                      <option value="String" data-id="element-560">String</option>
                      <option value="Number" data-id="element-561">Number</option>
                      <option value="Boolean" data-id="element-562">Boolean</option>
                      <option value="Object" data-id="element-563">Object</option>
                      <option value="Array" data-id="element-564">Array</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            {/* Customer parent field */}
            <div className="mb-2" data-id="element-565">
              <div className="grid grid-cols-2 gap-4" data-id="element-566">
                <div data-id="element-567">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-568">
                    Field Path
                  </label>
                  <input type="text" value="customer" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-569" />
                </div>
                <div data-id="element-570">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-571">
                    Type
                  </label>
                  <select value="Object" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-572">
                    <option value="String" data-id="element-573">String</option>
                    <option value="Number" data-id="element-574">Number</option>
                    <option value="Boolean" data-id="element-575">Boolean</option>
                    <option value="Object" data-id="element-576">Object</option>
                    <option value="Array" data-id="element-577">Array</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Customer child fields */}
            <div className="border border-gray-200 rounded-md p-4" data-id="element-578">
              {/* customer.id field */}
              <div className="mb-4" data-id="element-579">
                <div className="grid grid-cols-2 gap-4" data-id="element-580">
                  <div data-id="element-581">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-582">
                      Field Path
                    </label>
                    <input type="text" value="customer.id" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-583" />
                  </div>
                  <div data-id="element-584">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-585">
                      Type
                    </label>
                    <select value="String" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-586">
                      <option value="String" data-id="element-587">String</option>
                      <option value="Number" data-id="element-588">Number</option>
                      <option value="Boolean" data-id="element-589">Boolean</option>
                      <option value="Object" data-id="element-590">Object</option>
                      <option value="Array" data-id="element-591">Array</option>
                    </select>
                  </div>
                </div>
              </div>
              {/* customer.name field */}
              <div className="mb-4" data-id="element-592">
                <div className="grid grid-cols-2 gap-4" data-id="element-593">
                  <div data-id="element-594">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-595">
                      Field Path
                    </label>
                    <input type="text" value="customer.name" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-596" />
                  </div>
                  <div data-id="element-597">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-598">
                      Type
                    </label>
                    <select value="String" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-599">
                      <option value="String" data-id="element-600">String</option>
                      <option value="Number" data-id="element-601">Number</option>
                      <option value="Boolean" data-id="element-602">Boolean</option>
                      <option value="Object" data-id="element-603">Object</option>
                      <option value="Array" data-id="element-604">Array</option>
                    </select>
                  </div>
                </div>
              </div>
              {/* customer.email field */}
              <div data-id="element-605">
                <div className="grid grid-cols-2 gap-4" data-id="element-606">
                  <div data-id="element-607">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-608">
                      Field Path
                    </label>
                    <input type="text" value="customer.email" readOnly className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-609" />
                  </div>
                  <div data-id="element-610">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-611">
                      Type
                    </label>
                    <select value="String" className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-612">
                      <option value="String" data-id="element-613">String</option>
                      <option value="Number" data-id="element-614">Number</option>
                      <option value="Boolean" data-id="element-615">Boolean</option>
                      <option value="Object" data-id="element-616">Object</option>
                      <option value="Array" data-id="element-617">Array</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Constant Fields Section */}
      {showInferredFields && <div className="mt-8" data-id="element-618">
          <div className="flex justify-between items-center mb-4" data-id="element-619">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-620">
              Constant Fields
            </h3>
            <Button variant="secondary" size="sm" icon={<PlusIcon size={16} data-id="element-622" />} onClick={() => setShowAddConstantModal(true)} data-id="element-621">
              Add Constant Field
            </Button>
          </div>
          {constantFields.length > 0 ? <div className="space-y-4" data-id="element-623">
              {constantFields.map((field, index) => <div key={index} className="border border-gray-200 rounded-md p-4" data-id="element-624">
                  <div className="grid grid-cols-3 gap-4" data-id="element-625">
                    <div data-id="element-626">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-627">
                        Field Name
                      </label>
                      <input type="text" value={field.name} readOnly className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-628" />
                    </div>
                    <div data-id="element-629">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-630">
                        Type
                      </label>
                      <input type="text" value={field.type} readOnly className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-631" />
                    </div>
                    <div data-id="element-632">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-633">
                        Value
                      </label>
                      <input type="text" value={field.value} readOnly className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-634" />
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-4 border border-gray-200 rounded-md" data-id="element-635">
              <p className="text-sm text-gray-500" data-id="element-636">
                No constant fields added yet
              </p>
            </div>}
        </div>}
      {/* Formula Fields Section */}
      {showInferredFields && <div className="mt-8" data-id="element-637">
          <div className="flex justify-between items-center mb-4" data-id="element-638">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-639">
              Formula Fields
            </h3>
            <Button variant="secondary" size="sm" icon={<PlusIcon size={16} data-id="element-641" />} onClick={() => setShowAddFormulaModal(true)} data-id="element-640">
              Add Formula Field
            </Button>
          </div>
          {formulaFields.length > 0 ? <div className="space-y-4" data-id="element-642">
              {formulaFields.map((field, index) => <div key={index} className="border border-gray-200 rounded-md p-4" data-id="element-643">
                  <div className="grid grid-cols-3 gap-4" data-id="element-644">
                    <div data-id="element-645">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-646">
                        Field Name
                      </label>
                      <input type="text" value={field.name} readOnly className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-647" />
                    </div>
                    <div data-id="element-648">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-649">
                        Type
                      </label>
                      <input type="text" value={field.type} readOnly className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-650" />
                    </div>
                    <div data-id="element-651">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-652">
                        Formula
                      </label>
                      <input type="text" value={field.formula} readOnly className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-653" />
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-4 border border-gray-200 rounded-md" data-id="element-654">
              <p className="text-sm text-gray-500" data-id="element-655">
                No formula fields added yet
              </p>
            </div>}
        </div>}
      {/* Add Constant Field Modal */}
      <AddConstantModal isOpen={showAddConstantModal} onClose={() => setShowAddConstantModal(false)} onAdd={handleAddConstantField} data-id="element-656" />
      {/* Add Formula Field Modal */}
      <AddFormulaModal isOpen={showAddFormulaModal} onClose={() => setShowAddFormulaModal(false)} onAdd={handleAddFormulaField} data-id="element-657" />
    </div>;
};